import xpjsService from './xpjsService'
import GeolocationManager from '@/library/geolocation/services/geolocationManager'
import config from '@/config'
import Vue from 'vue'
import utils from '../../common/utils'
import helpers from '@/library/geolocation/services/providers/xpoint/helpers'

let geoManager = null
const isDebug = sessionStorage.geolocationDebug
const GEOLOCATION_CONFIG = config.app.autoconf.GEOLOCATION
const PLUGIN_BASE_URL = GEOLOCATION_CONFIG && GEOLOCATION_CONFIG.XPOINT && GEOLOCATION_CONFIG.XPOINT.PLUGIN_BASE_URL
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
const OS = {
  ANDROID: 'Android',
  IOS: 'iOS',
  WINDOWS: 'Windows',
  MAC: 'Mac'
}

/**
 * Checks the user's geolocation verification status using the XPoint SDK.
 * If the status is "WAITING", it will automatically retry until a final response is received.
 * Handles allowed, denied, and session-expired cases.
 */
async function checkStatus (user, sessionId = false, reason = false, CLIENT_KEY) {
  const options = {
    userId: user,
    client: CLIENT_KEY
  }

  try {
    let data = await xpjsService.checkStatus(options)
    // Retry loop for "WAITING" state until final status is received
    while (data.status === 'WAITING') {
      if (isDebug) console.log('[XPoint] status: WAITING — retrying...')
      await sleep(1000)
      data = await xpjsService.checkStatus(options)
    }
    if (data.status === 'DENIED' && helpers.has5010Error(data)) {
      disconnectService(true)
      if (isDebug) console.log('[XPoint] Session has expired.')
      await sleep(2000)
      return checkStatus(user, false, reason, CLIENT_KEY)
    }
    // Validate geolocation result
    if (isDebug) console.log('[XPoint] Check status responded:', data)
    if (data.jwt && user) managerInstance.geolocationManager.validateGeolocation(data.jwt, user)
    return true
  } catch (e) {
    if (isDebug) console.error('[XPoint] checkStatus error:', e)
    return false
  }
}

/**
 * Disconnects the XPoint SDK service by unsubscribing from status updates.
 * Used when stopping geolocation tracking or cleaning up before logout.
 */
function disconnectService (forceAll = false) {
  xpjsService.unsubscribe(forceAll)
}

/**
 * Checks whether the XPoint Verify app/plugin is active and available on the client device.
 * If not installed, triggers an error handler for the user.
 */
async function checkPermissionOrPlugin (user, sessionId, reason) {
  try {
    const isVerifyActive = await xpjsService.isVerifyActive()
    if (!isVerifyActive) {
      if (isDebug && reason === 'Login') console.log('[XPoint] Verify App not installed.')
      utils.errorHandler('ERROR_VERIFY_APP', 'Verify App not installed or inactive.', user, reason)
      return false
    }
    if (isDebug && reason === 'Login') console.log('[XPoint] Verify App is active.')
    return true
  } catch (e) {
    console.error('[XPoint] checkPermissionOrPlugin failed:', e)
    return false
  }
}

/**
 * Subscribes to XPoint status updates and reacts to changes in user geolocation status.
 * Called automatically after successful geolocation verification.
 */
function startStatusUpdate (options) {
  const handler = async (checkRequest) => {
    switch (checkRequest.status) {
      case 'ALLOWED':
        if (checkRequest.jwt && checkRequest.userId) {
          if (isDebug) console.log('[XPoint] Location allowed.')
          managerInstance.geolocationManager.validateGeolocation(checkRequest.jwt, options.userId)
        }
        break
      case 'DENIED':
        if (checkRequest.jwt && checkRequest.userId) {
          if (isDebug) console.warn('[XPoint] Location denied.')
          managerInstance.geolocationManager.validateGeolocation(checkRequest.jwt, options.userId)
        }
        break
      case 'WAITING':
        if (isDebug) console.log('[XPoint] Waiting for verification...')
        break

      case 'IDLE':
        if (isDebug) console.warn('[XPoint] Verify inactive, prompt user to reopen app.')
        utils.errorHandler('ERROR_VERIFY_APP', 'Verify App not installed or inactive.', options.user, 'UPDATE')
        disconnectService()
        break

      default:
        if (isDebug) console.warn('[XPoint] Unknown status:', checkRequest)
        utils.errorHandler('Unknown_status', checkRequest.status, options.user, 'UPDATE')
        disconnectService()
    }
  }
  if (isDebug) console.log('[XPoint] Subscribing to Xpoint status changes.')
  xpjsService.subscribe(options, handler)
}

/**
 * Initiates download of the XPoint Verify plugin or installer based on the user's platform.
 * Automatically redirects to the proper installer URL.
 */
function download () {
  const baseUrl = new URL(PLUGIN_BASE_URL).href
  const downloadUrlByPlatform = {
    [OS.ANDROID]: 'verify-playstore.lnk',
    [OS.IOS]: 'ios-appstore.lnk',
    [OS.MAC]: 'XpointVerifyGeolocationInstaller.pkg',
    [OS.WINDOWS]: 'xpoint-web-sdk.exe'
  }
  const platformName = getPlatform()
  const app = downloadUrlByPlatform[platformName]
  let newUrl = new URL(app, baseUrl).href
  if (newUrl) {
    if (isDebug) console.log('[XPOINT] plugin URL:', newUrl)
    window.location.href = newUrl
  }
}

/**
 * Detects the user's current operating system to determine which
 * plugin or installer to serve for XPoint Verify.
 */
const getPlatform = () => {
  let platformName = OS.WINDOWS
  const userAgent = window.navigator.userAgent
  const platform = window.navigator.platform

  if (/Android/.test(userAgent)) platformName = OS.ANDROID
  if (
    /iPhone/.test(platform) ||
    /iPad/.test(platform) ||
    /iOS/.test(platform) ||
    /(iPad)/.test(userAgent) ||
    (platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  ) {
    platformName = OS.IOS
  }
  if (/Win/.test(platform)) platformName = OS.WINDOWS
  if (/Mac/.test(platform)) platformName = OS.MAC

  return platformName
}

/**
 * Provides a singleton instance of GeolocationManager.
 * Ensures that only one instance is active throughout the app lifecycle.
 */
const managerInstance = {
  get geolocationManager () {
    if (!geoManager) geoManager = GeolocationManager.getInstance(config.app.autoconf.GEOLOCATION.PROVIDER, Vue.prototype.$geolocationDebug)
    return geoManager
  }
}

export default {
  checkStatus,
  disconnectService,
  checkPermissionOrPlugin,
  download,
  startStatusUpdate
}



// WEBPACK FOOTER //
// ./src/library/geolocation/services/providers/xpoint/verify.js